import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import UserButton from '../auth/UserButton';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../../context/LanguageContext';

// Navigation structure (keys only, labels come from translations)
const navStructure = [
  { key: 'home', href: '#home' },
  { key: 'about', href: '#about' },
  { key: 'services', href: '#services' },
  { key: 'celebrant', href: '/celebrante', isPage: true },
  { key: 'simulator', href: '/simulador', isPage: true },
  { key: 'tools', href: '/ferramentas', isPage: true },
  { key: 'portfolio', href: '#portfolio' },
  { key: 'testimonials', href: '#testimonials' },
  { key: 'contact', href: '#contact' }
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  // Get translated navigation links
  const navigationLinks = navStructure.map(item => ({
    ...item,
    name: t(`nav.${item.key}`)
  }));

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle hash scroll after navigation
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    
    if (link.isPage) {
      navigate(link.href);
    } else if (link.href.startsWith('#')) {
      const targetId = link.href;
      
      if (location.pathname !== '/') {
        // Navigate to home first, then scroll
        navigate('/' + targetId);
      } else {
        // Already on home page, scroll directly
        const element = document.querySelector(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-[#FAF9F7]/95 backdrop-blur-md shadow-sm py-4'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={handleLogoClick}
            className="group text-left"
            data-testid="logo-button"
          >
            <span className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light text-[#2C2C2C] tracking-wide">
              Alma Leve
            </span>
            <div className="flex items-center gap-2 mt-[-2px]">
              <span className="text-[0.65rem] tracking-[0.25em] uppercase text-[#8B7D6B] font-medium">
                Weddings
              </span>
              <span className="text-[0.55rem] text-[#C4B5A0] italic font-['Cormorant_Garamond']">
                by Véronique
              </span>
            </div>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <ul className="flex items-center gap-6">
              {navigationLinks.map((link) => (
                <li key={link.key}>
                  <button
                    onClick={() => handleNavClick(link)}
                    className={`nav-link ${link.isPage ? 'text-[#C4B5A0] font-medium' : ''}`}
                    data-testid={`nav-${link.key}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
            <LanguageSelector />
            <UserButton />
          </div>

          {/* Mobile: User Button + Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSelector />
            <UserButton />
            <button
              className="p-2 text-[#2C2C2C] hover:text-[#8B7D6B]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden absolute top-full left-0 right-0 bg-[#FAF9F7]/98 backdrop-blur-md shadow-lg transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <ul className="py-6 px-6 space-y-4">
            {navigationLinks.map((link) => (
              <li key={link.key}>
                <button
                  onClick={() => handleNavClick(link)}
                  className={`block py-2 text-sm tracking-widest uppercase text-[#2C2C2C] hover:text-[#C4B5A0] transition-colors w-full text-left ${link.isPage ? 'text-[#C4B5A0] font-medium' : ''}`}
                  data-testid={`mobile-nav-${link.key}`}
                >
                  {link.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;
